// Sample Character Form Component (Refactored with Audio Hook)
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Icon, Grid, Message } from 'semantic-ui-react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikField
} from '../FormComponents';
import AvatarPicker from '../../../../components/AvatarPicker';
import AudioRecorderSection from './AudioRecorderSection';
import { IMAGE_CONFIG } from '../../../../constants/imageConfig';
import useMediaUpload from '../../../../hooks/useMediaUpload';
import useAudioManager from '../../../../hooks/useAudioManager';
// Import vaUtilitiesSlice thunks for voice and text upload
import {
  uploadVoiceSnippet,
  uploadTextSnippet,
  createSpeechSampleRecord
} from '../../../../voiceActorWorksheet/slices/vaUtilitiesSlice';
import { selectPrimaryPortfolio } from '../../../../slices/sampleCharacterSlice';

import '../../../../styles/sample-voice-pages.css';
import '../../../../index.css';

// Character type options
const characterTypeOptions = [
  { key: 'narrator', text: 'Narrator', value: 'narrator' },
  { key: 'hero', text: 'Hero', value: 'hero' },
  { key: 'villain', text: 'Villain', value: 'villain' },
  { key: 'child', text: 'Child', value: 'child' },
  { key: 'elderly', text: 'Elderly', value: 'elderly' },
  { key: 'comic', text: 'Comic Relief', value: 'comic' },
  { key: 'mentor', text: 'Mentor', value: 'mentor' },
  { key: 'general', text: 'General', value: 'general' }
];

// Genre options
const genreOptions = [
  { key: 'fantasy', text: 'Fantasy', value: 'fantasy' },
  { key: 'scifi', text: 'Sci-Fi', value: 'scifi' },
  { key: 'mystery', text: 'Mystery', value: 'mystery' },
  { key: 'romance', text: 'Romance', value: 'romance' },
  { key: 'thriller', text: 'Thriller', value: 'thriller' },
  { key: 'drama', text: 'Drama', value: 'drama' },
  { key: 'comedy', text: 'Comedy', value: 'comedy' },
  { key: 'documentary', text: 'Documentary', value: 'documentary' },
  { key: 'educational', text: 'Educational', value: 'educational' },
  { key: 'general', text: 'General', value: 'general' }
];

// Gender options
const genderOptions = [
  { key: 'male', text: 'Male', value: 'male' },
  { key: 'female', text: 'Female', value: 'female' },
  { key: 'neutral', text: 'Neutral', value: 'neutral' },
  { key: 'other', text: 'Other', value: 'other' }
];

// Age options
const ageOptions = [
  { key: 'child', text: 'Child', value: 'child' },
  { key: 'teen', text: 'Teen', value: 'teen' },
  { key: 'young-adult', text: 'Young Adult', value: 'young-adult' },
  { key: 'adult', text: 'Adult', value: 'adult' },
  { key: 'middle-aged', text: 'Middle Aged', value: 'middle-aged' },
  { key: 'elderly', text: 'Elderly', value: 'elderly' }
];

// Validation schema
const sampleCharacterSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('Character name is required'),
  role: Yup.string(),
  description: Yup.string(),
  characterType: Yup.string(),
  genre: Yup.string(),
  gender: Yup.string(),
  age: Yup.string(),
  scriptContent: Yup.string()
    .trim()
    .required('Script content is required')
});

const SampleCharacterFormRefactored = ({
  character,
  isCreatingNew,
  newCharacterId,
  onSave,
  onCancel,
  loading,
  portfolioId
}) => {
  const dispatch = useDispatch();

  // Avatar state management
  const [avatarData, setAvatarData] = useState(null);
  const [avatarUploadStatus, setAvatarUploadStatus] = useState('idle');
  const [avatarUploadResult, setAvatarUploadResult] = useState(null);
  const [newCharacterUuid, setNewCharacterUuid] = useState(null);
  const [formChanged, setFormChanged] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  // Default variation state for sample characters
  const [defaultVariationId, setDefaultVariationId] = useState(null);

  // Refs
  const formikRef = useRef(null);

  // Get user profile and portfolio from Redux
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.data?.userId || '00000000-0000-0000-0000-000000000000';
  const actorId = userProfile?.data?.actorId || userId;
  const primaryPortfolio = useSelector(selectPrimaryPortfolio);

  // Media upload hook
  const { uploadMedia } = useMediaUpload();

  // Initialize audio manager with existing data
  // Using consistent 'sample' structure
  let existingAudioData = null;

  if (!isCreatingNew && character?.sample) {
    console.log('[SAMPLE CHARACTER FORM] Full character object:', JSON.stringify(character, null, 2));
    console.log('[SAMPLE CHARACTER FORM] sample:', character.sample);

    // Get audio data from the consistent sample structure
    if (character.sample.voiceS3Url) {
      existingAudioData = {
        voiceS3Url: character.sample.voiceS3Url,
        fileDuration: character.sample.fileDuration,
        voiceFileName: character.sample.voiceFileName
      };
      console.log('[SAMPLE CHARACTER FORM] Using sample audio data:', existingAudioData);
    } else {
      console.log('[SAMPLE CHARACTER FORM] No audio data found in sample');
    }
  } else {
    console.log('[SAMPLE CHARACTER FORM] isCreatingNew:', isCreatingNew, 'character:', character);
  }

  const audioManager = useAudioManager(existingAudioData, userId);

  // Generate a new UUID for new characters
  useEffect(() => {
    if (isCreatingNew && !newCharacterUuid) {
      const uuid = uuidv4();
      setNewCharacterUuid(uuid);
    }
  }, [isCreatingNew, newCharacterUuid]);

  // Initialize variation ID
  useEffect(() => {
    if (character && !isCreatingNew) {
      const speechSample = character.sample || {};

      if (speechSample.variationId) {
        setDefaultVariationId(speechSample.variationId);
      } else {
        const newVariationId = uuidv4();
        setDefaultVariationId(newVariationId);
      }
    }
  }, [character, isCreatingNew]);

  // Reset upload status after successful completion
  useEffect(() => {
    if (avatarUploadStatus === 'success') {
      const timer = setTimeout(() => {
        setAvatarUploadStatus('idle');
        setAvatarUploadResult(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [avatarUploadStatus]);

  // Initial values for Formik
  const initialValues = {
    name: character?.name || '',
    description: character?.description || '',
    role: character?.role || '',
    characterType: character?.characterType || 'general',
    genre: character?.genre || 'general',
    gender: character?.gender || '',
    age: character?.age || '',
    tags: character?.tags || [],
    scriptContent: character?.sample?.textContent || '',
    priority: character?.priority || 'medium',
    status: character?.status || 'active',
    avatarUrl: character?.avatarUrl || IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl
  };

  // Avatar change handler
  const handleAvatarChange = useCallback((avatarChangeData) => {
    console.log('[SAMPLE CHARACTER FORM] Avatar changed:', avatarChangeData);
    setAvatarData(avatarChangeData);

    if (formikRef.current && !avatarChangeData.url.startsWith('blob:')) {
      formikRef.current.setFieldValue('avatarUrl', avatarChangeData.url);
      formikRef.current.setFieldTouched('avatarUrl', true);
    }

    setFormChanged(avatarChangeData.hasChanges);
  }, []);

  // Handle upload completion from AvatarPicker
  const handleUploadComplete = useCallback((uploadResult) => {
    console.log('[SAMPLE CHARACTER FORM] Avatar upload completed:', uploadResult);
    if (formikRef.current) {
      formikRef.current.setFieldValue('avatarUrl', uploadResult.url);
    }
  }, []);

  // Handle upload errors from AvatarPicker
  const handleUploadError = useCallback((error) => {
    console.error('[SAMPLE CHARACTER FORM] Avatar upload error:', error);
    setSubmissionError('Failed to upload avatar: ' + (error.message || error));
  }, []);

  // Audio change handler
  const handleAudioChange = useCallback((audioChangeData) => {
    // Only set form as changed if there's actual new audio, not just when Replace Audio is clicked
    if (audioChangeData.hasNewAudio) {
      setFormChanged(true);
    }
  }, []);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    console.log('[FORM SUBMIT] handleSubmit called with values:', values);
    setSubmissionError(null);
    const { state: audioState } = audioManager;

    try {
      // Handle avatar upload if needed
      let finalAvatarUrl = values.avatarUrl;
      if (avatarData?.file && avatarData?.hasChanges && !avatarData?.isUploaded) {
        try {
          console.log('[SAMPLE CHARACTER FORM] Uploading avatar...');
          const uploadUrl = await uploadMedia(
            avatarData.file,
            'vaSampleCharacterAvatar',
            {
              userId: userId,
              actorId: actorId,
              portfolioId: portfolioId || primaryPortfolio?.uuid,
              characterId: character?.uuid || newCharacterUuid
            },
            userId
          );

          finalAvatarUrl = uploadUrl;
          setAvatarUploadStatus('success');
          setAvatarUploadResult({
            url: uploadUrl,
            timestamp: new Date(),
            originalFile: avatarData.file
          });
        } catch (err) {
          console.error('[SAMPLE CHARACTER FORM] Avatar upload failed:', err);
          setSubmissionError('Failed to upload avatar: ' + err.message);
          setSubmitting(false);
          return;
        }
      } else if (avatarData?.isUploaded) {
        finalAvatarUrl = avatarData.url;
      }

      // Update values with final avatar URL
      const updatedValues = {
        ...values,
        avatarUrl: finalAvatarUrl
      };

      // Prepare audio data for parent save handler
      const audioData = audioState.audioBlob ? {
        blob: audioState.audioBlob,
        duration: audioState.recordingDuration,
        source: audioState.audioSource,
        fileName: audioState.uploadedAudioName
      } : null;

      // Save character data
      console.log('[SAMPLE CHARACTER FORM] Calling onSave with updatedValues:', updatedValues);
      console.log('[SAMPLE CHARACTER FORM] Calling onSave with audioData:', audioData);

      const characterResult = await onSave(updatedValues, audioData);

      console.log('[SAMPLE CHARACTER FORM] onSave returned:', characterResult);
      console.log('[SAMPLE CHARACTER FORM] typeof characterResult:', typeof characterResult);

      // If onSave handled everything (returned true), we're done!
      if (characterResult === true) {
        console.log('[SAMPLE CHARACTER FORM] onSave handled everything, skipping duplicate uploads');

        // Clear audio state after successful save
        audioManager.actions.deleteAudio();

        // Reset form state
        setFormChanged(false);
        setSubmitting(false);
        resetForm({ values });
        return;
      }

      // Only continue with manual uploads if onSave returned a character object (legacy flow)
      if (characterResult && typeof characterResult === 'object') {
        console.log('[SAMPLE CHARACTER FORM] onSave returned an object, continuing with manual uploads (legacy)');
        // Get the characterId from result
        const characterId = characterResult.uuid || character?.uuid || newCharacterUuid;
        const finalPortfolioId = portfolioId || primaryPortfolio?.uuid;

        // Get or create variation ID
        let variationId = defaultVariationId;
        if (!variationId) {
          variationId = uuidv4();
          setDefaultVariationId(variationId);
        }

        // Prepare metadata for uploads
        const uploadMetadata = {
          actorId: actorId,
          portfolioId: finalPortfolioId,
          characterId: characterId,
          variationName: 'Sample Voice',
          isSampleCharacter: true
        };

        // Handle speech sample creation/update
        const speechSampleId = (!isCreatingNew && character?.sample?.speechSampleId)
          ? character.sample.speechSampleId
          : uuidv4();

        // Preserve existing data if not replacing
        let existingVoiceS3Url = (!isCreatingNew && character?.sample?.voiceS3Url && !audioState.audioBlob)
          ? character.sample.voiceS3Url
          : null;
        let existingTextS3Url = (!isCreatingNew && character?.sample?.textS3Url)
          ? character.sample.textS3Url
          : null;
        let existingFileDuration = (!isCreatingNew && character?.sample?.fileDuration && !audioState.audioBlob)
          ? character.sample.fileDuration
          : null;

        try {
          let voiceUploadResult = null;
          let textUploadResult = null;

          // Upload voice snippet if we have NEW audio
          if (audioState.audioBlob) {
            console.log('[SAMPLE CHARACTER FORM] Uploading voice snippet...');
            const voiceResult = await dispatch(uploadVoiceSnippet({
              audioBlob: audioState.audioBlob,
              variationId: variationId,
              speechSampleId: speechSampleId,
              metadata: {
                ...uploadMetadata,
                fileDuration: audioState.recordingDuration
              },
              uploadMedia: uploadMedia
            })).unwrap();

            voiceUploadResult = voiceResult;
            existingVoiceS3Url = null;
            existingFileDuration = null;
          }

          // Upload text snippet if content changed
          const scriptContentChanged = values.scriptContent !== character?.sample?.textContent;
          if (values.scriptContent && values.scriptContent.trim() && (isCreatingNew || scriptContentChanged)) {
            console.log('[SAMPLE CHARACTER FORM] Uploading text snippet...');
            const textResult = await dispatch(uploadTextSnippet({
              textContent: values.scriptContent,
              variationId: variationId,
              speechSampleId: speechSampleId,
              metadata: uploadMetadata,
              uploadMedia: uploadMedia
            })).unwrap();

            textUploadResult = textResult;
            existingTextS3Url = null;
          }

          // Create/update speech sample record
          const shouldCreateSpeechSample = voiceUploadResult || textUploadResult ||
            (existingVoiceS3Url && scriptContentChanged) ||
            (existingTextS3Url && audioState.audioBlob);

          if (shouldCreateSpeechSample) {
            const finalVoiceUrl = voiceUploadResult?.voiceS3Url || existingVoiceS3Url;
            const finalTextUrl = textUploadResult?.textS3Url || existingTextS3Url;
            const finalFileDuration = audioState.audioBlob ? audioState.recordingDuration : (existingFileDuration || null);

            await dispatch(createSpeechSampleRecord({
              speechSampleData: {
                speechSampleId: speechSampleId,
                variationId: variationId,
                variationName: 'Sample Voice',
                version: 1,
                voiceS3Url: finalVoiceUrl,
                textS3Url: finalTextUrl,
                textContent: values.scriptContent || '',
                fileDuration: finalFileDuration,
                metadata: {
                  ...uploadMetadata,
                  characterName: values.name,
                  characterDescription: values.description,
                  characterAvatarUrl: finalAvatarUrl
                }
              }
            })).unwrap();
          }

          if (isCreatingNew) {
            resetForm();
            audioManager.actions.deleteAudio();
            setAvatarData(null);
            setFormChanged(false);
            setDefaultVariationId(null);
          }
        } catch (uploadError) {
          console.error('[SAMPLE CHARACTER FORM] Upload error:', uploadError);
          setSubmissionError(`Character saved, but audio/text upload failed: ${uploadError.message}`);
        }
      }
    } catch (error) {
      console.error('[SAMPLE CHARACTER FORM] Submission error:', error);
      setSubmissionError(error.message || 'An error occurred while saving');
    }

    setSubmitting(false);
  };

  return (
    <div className="sample-character-form">
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={sampleCharacterSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, values, isValid, dirty, handleSubmit: formikHandleSubmit }) => (
          <Form>
            <div className="form-header">
              <h2 className="sectionTitleLight">
                <Icon name={isCreatingNew ? 'plus' : 'edit'} />
                {isCreatingNew ? 'New Sample Character' : 'Edit Sample Character'}
              </h2>
            </div>

            {/* Avatar Picker positioned top-right */}
            {/* <div style={{ backgroundColor: "transparent", position: "absolute", top: "5px", right: "210px", width: "100px", zIndex: "5000" }}>
              <AvatarPicker
                characterId={character?.uuid || newCharacterUuid || 'new'}
                currentAvatarUrl={character?.avatarUrl || IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl}
                onAvatarChange={handleAvatarChange}
                onUploadComplete={handleUploadComplete}
                onUploadError={handleUploadError}
                resourceType="vaSampleCharacterAvatar"
                uploadParams={{
                  userId: userId,
                  actorId: actorId,
                  portfolioId: portfolioId || primaryPortfolio?.uuid,
                  characterId: character?.uuid || newCharacterUuid
                }}
                showHints={true}
                hintLocation={"top"}
                autoUpload={false}
                uploadStatus={avatarUploadStatus}
                uploadResult={avatarUploadResult}
              />
            </div> */}

            <div className="form-content">
              {/* Character Details Section */}
              <div className="form-section">
                <h3>Character Details</h3>
                <Grid>
                  <Grid.Row columns={1}>
                    <Grid.Column>
                      <FormikField
                        name="name"
                        component={FormikInput}
                        label="Character Name *"
                        fluid
                        placeholder="Enter character name"
                      />
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className='marginTop_neg_30' columns={1}>
                    <Grid.Column>
                      <FormikField
                        name="role"
                        component={FormikInput}
                        label="Role/Title"
                        fluid
                        placeholder="e.g., Main Protagonist, Narrator"
                      />
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className='marginTop_neg_30' columns={2}>
                    <Grid.Column>
                      <FormikField
                        name="characterType"
                        component={FormikSelect}
                        label="Character Type"
                        options={characterTypeOptions}
                        placeholder="Select character type"
                        fluid
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <FormikField
                        name="genre"
                        component={FormikSelect}
                        label="Genre"
                        options={genreOptions}
                        placeholder="Select genre"
                        fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </div>

              {/* Voice Characteristics Section */}
              <div className="form-section">
                <h3>Voice Characteristics</h3>

                <Grid>
                  <Grid.Row columns={2}>
                    <Grid.Column width={8} style={{ zIndex: "5000" }}>
                      <FormikField
                        name="gender"
                        component={FormikSelect}
                        label="Gender"
                        options={genderOptions}
                        placeholder="Select gender"
                        fluid

                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      {/* <div style={{ backgroundColor: "transparent", position: "absolute", top: "5px", right: "210px", width: "100px", zIndex: "5000" }}> */}

                      <div style={{ backgroundColor: "transparent", position: "absolute", top: "-45px", left: "-80px", zIndex: "5000", margin: "0" }}>
                        <AvatarPicker
                          characterId={character?.uuid || newCharacterUuid || 'new'}
                          currentAvatarUrl={character?.avatarUrl || IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl}
                          onAvatarChange={handleAvatarChange}
                          onUploadComplete={handleUploadComplete}
                          onUploadError={handleUploadError}
                          resourceType="vaSampleCharacterAvatar"
                          uploadParams={{
                            userId: userId,
                            actorId: actorId,
                            portfolioId: portfolioId || primaryPortfolio?.uuid,
                            characterId: character?.uuid || newCharacterUuid
                          }}
                          showHints={true}
                          hintLocation={"top"}
                          autoUpload={false}
                          uploadStatus={avatarUploadStatus}
                          uploadResult={avatarUploadResult}
                        />
                      </div>

                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row className='marginTop_neg_20' columns={1}>
                    <Grid.Column width={8}>
                      <FormikField
                        name="age"
                        component={FormikSelect}
                        label="Age Range"
                        options={ageOptions}
                        placeholder="Select age range"
                        fluid
                      />
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className='marginTop_neg_20' columns={1}>
                    <Grid.Column>
                      <FormikField
                        name="description"
                        component={FormikTextArea}
                        label="Description"
                        placeholder="Describe the character's voice, personality, or other characteristics"
                        rows={3}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </div>

              {/* Script Content Section */}
              {/* <div className="form-section"  > */}
              < div className="recording-script-section">
                <FormikField
                  name="scriptContent"
                  component={FormikTextArea}
                  label="Script Content *"
                  placeholder="Enter the script or text this character will read"
                  rows={5}
                />
              </div>

              {/* Audio Recording Section - Using the new component */}
              <AudioRecorderSection
                audioManager={audioManager}
                isCreatingNew={isCreatingNew}
                onAudioChange={handleAudioChange}
              />

              {/* Error Messages */}
              {submissionError && (
                <Message negative>
                  <Message.Header>Error</Message.Header>
                  <p>{submissionError}</p>
                </Message>
              )}
            </div>

            {/* Form Footer */}
            <div className="form-footer" style={{ textAlign: 'right', marginTop: '10px', paddingTop: '10px' }}>
              <Button onClick={onCancel} style={{ marginRight: '10px' }}>
                Cancel
              </Button>
              <Button
                primary
                type="submit"
                loading={loading || isSubmitting}
                disabled={!isValid || (!dirty && !formChanged)}
              >
                <Icon name="save" />
                {isCreatingNew ? 'Create' : 'Update'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

SampleCharacterFormRefactored.propTypes = {
  character: PropTypes.object,
  isCreatingNew: PropTypes.bool,
  newCharacterId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  portfolioId: PropTypes.string
};

export default SampleCharacterFormRefactored;
